import Big from 'big.js';
import type { Currency } from '@/types/common';

/**
 * BİÇİMLENDİRMENİN TEK KAPISI.
 *
 * Bileşenlerde doğrudan `Intl.NumberFormat` çağrılmaz. Sebep: aynı tutarın
 * tabloda "1.234.567,89 ₺", grafikte "1,23 Mn ₺" ve dışa aktarımda başka türlü
 * görünmesi, kullanıcıda "sistem tutarsız" algısı yaratır. Tek kaynak, tek görünüm.
 *
 * Türkçe biçim kuralları:
 *   - Binlik ayracı NOKTA, ondalık ayracı VİRGÜL  → 1.234.567,89
 *   - Yüzde işareti sayının ÖNÜNDE                → %12,34
 *   - Tarih gün.ay.yıl                            → 15.03.2026
 */

const LOCALE = 'tr-TR';

// Intl nesneleri pahalıdır; her çağrıda yeniden kurmak yerine önbelleğe alınır.
const numberFormatters = new Map<string, Intl.NumberFormat>();

function getNumberFormatter(key: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  let f = numberFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(LOCALE, options);
    numberFormatters.set(key, f);
  }
  return f;
}

// -----------------------------------------------------------------------------
// Sayı
// -----------------------------------------------------------------------------

export interface NumberFormatOptions {
  decimals?: number;
  /** Ondalık basamak sayısı sabit tutulsun mu (1.000,00 gibi)? */
  fixed?: boolean;
}

export function formatNumber(
  value: number | string | null | undefined,
  { decimals = 0, fixed = false }: NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || value === '') return '—';

  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';

  return getNumberFormatter(`n:${decimals}:${fixed}`, {
    minimumFractionDigits: fixed ? decimals : 0,
    maximumFractionDigits: decimals,
  }).format(n);
}

// -----------------------------------------------------------------------------
// Para
// -----------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
};

export interface MoneyFormatOptions {
  /** Büyük tutarları kısalt: 1.234.567 → "1,23 Mn" */
  compact?: boolean;
  /** Para birimi simgesi gösterilsin mi? */
  withSymbol?: boolean;
  decimals?: number;
}

/**
 * Para biçimlendirir.
 *
 * `amount` STRING kabul eder çünkü API'den decimal string olarak gelir.
 * Number'a çevirmek 17 hanenin üzerinde hassasiyet kaybettirir ve
 * 0,01'lik fatura uyuşmazlıklarına yol açar.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency: Currency = 'TRY',
  { compact = false, withSymbol = true, decimals = 2 }: MoneyFormatOptions = {},
): string {
  if (amount === null || amount === undefined || amount === '') return '—';

  const raw = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(raw)) return '—';

  /*
    EKSİ SIFIR DİYE BİR TUTAR YOKTUR.

    Yuvarlanmış payların toplamı ana tutardan −0,000000001 sapabilir ve
    `Intl` bunu sadakatle "−0 ₺" diye yazar. Ekranda "İşletmede Kalan:
    −0 ₺" gören bir finans müdürü haklı olarak durur: eksi bir bakiye
    ne demek? Hiçbir şey — yalnızca kayan nokta artığı.

    Gösterilecek hane sayısına yuvarladıktan sonra sonuç sıfırsa, işareti
    de atıyoruz. Bu bir "hatayı gizleme" değil: 0,004 ₺ ile 0 ₺ arasındaki
    fark zaten gösterilmiyor, dolayısıyla işaretin de bir anlamı kalmıyor.
  */
  const rounded = Number(raw.toFixed(decimals));
  const n = rounded === 0 ? 0 : raw;

  const symbol = CURRENCY_SYMBOLS[currency];

  if (compact) {
    return `${formatCompactNumber(n)}${withSymbol ? ` ${symbol}` : ''}`;
  }

  const formatted = getNumberFormatter(`m:${decimals}`, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

  // Simge SONDA: Türkiye'de resmi ve yaygın yazım "1.234,56 ₺" biçimindedir.
  // Kısaltılmış gösterimle ("5,82 Mr ₺") de tutarlı olması için tek kural.
  return withSymbol ? `${formatted} ${symbol}` : formatted;
}

/**
 * Büyük sayıları Türkçe kısaltmalarla gösterir.
 *
 * Intl'in `notation: 'compact'` seçeneği tr-TR'de "1,2 Mn" yerine
 * beklenmedik çıktılar verebildiği için kısaltmalar elle uygulanıyor.
 */
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000) return `${formatNumber(value / 1_000_000_000, { decimals: 2 })} Mr`;
  if (abs >= 1_000_000) return `${formatNumber(value / 1_000_000, { decimals: 2 })} Mn`;
  if (abs >= 1_000) return `${formatNumber(value / 1_000, { decimals: 1 })} B`;

  return formatNumber(value, { decimals: 0 });
}

// -----------------------------------------------------------------------------
// Yüzde
// -----------------------------------------------------------------------------

/**
 * Yüzde biçimlendirir. Yüzde işareti Türkçede sayının ÖNÜNDE yazılır.
 *
 * @param value      Ham değer
 * @param asFraction `true` ise 0,1234 → "%12,34"; `false` ise 12,34 → "%12,34"
 */
export function formatPercent(
  value: number | string | null | undefined,
  { decimals = 2, asFraction = false }: { decimals?: number; asFraction?: boolean } = {},
): string {
  if (value === null || value === undefined || value === '') return '—';

  const raw = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(raw)) return '—';

  const n = asFraction ? raw * 100 : raw;
  return `%${formatNumber(n, { decimals, fixed: decimals > 0 })}`;
}

// -----------------------------------------------------------------------------
// Alan
// -----------------------------------------------------------------------------

export function formatArea(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return `${formatNumber(value, { decimals: 0 })} m²`;
}

// -----------------------------------------------------------------------------
// Tarih
// -----------------------------------------------------------------------------

const TR_MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
] as const;

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const d = typeof value === 'string' ? parseLocalDate(value) : value;
  if (!d || Number.isNaN(d.getTime())) return '—';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

/**
 * Tarih + saat: "06.09.2026 12:34:21"
 *
 * NEDEN SANİYE DAHİL: Bu biçim işlem zamanı gösteren yerlerde kullanılır
 * (denetim kaydı, beyan gönderimi, tahsilat, olay kaydı). Aynı dakika
 * içinde birden çok işlem olduğunda, saniye olmadan sıralama görünmez
 * hale gelir ve "hangisi önce oldu?" sorusu cevapsız kalır — denetimde
 * en çok sorulan sorulardan biri budur.
 *
 * TEK BİR ZAMAN DAMGASI BİÇİMİ VAR: bu.
 *
 * Bir dönem saniyesiz bir varyant da (`formatDateShortTime`) duruyordu ama
 * hiçbir yerde kullanılmıyordu. İki biçim arasında seçim yapmak, ekrandan
 * ekrana farklı görünen zaman damgaları üretir ve "bu tarih neden başka
 * yazılmış?" sorusunu doğurur — denetim ekranında bu soru pahalıdır.
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';

  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');

  return `${formatDate(d)} ${hh}:${mm}:${ss}`;
}

/**
 * Göreli zaman: "3 dakika önce", "2 saat önce", "5 gün önce".
 *
 * Yakın geçmişteki olaylarda mutlak tarihten daha okunaklıdır; 7 günden
 * eskiyse mutlak tarihe düşer — "47 gün önce" kimsenin zihninde bir
 * tarihe karşılık gelmez.
 */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';

  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60_000);

  if (minutes < 1) return 'az önce';
  if (minutes < 60) return `${minutes} dakika önce`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} saat önce`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} gün önce`;

  return formatDate(d);
}

/** "2026-03" → "Mart 2026" */
export function formatMonthYear(period: string | null | undefined): string {
  if (!period) return '—';

  const [y, m] = period.split('-');
  const monthIndex = Number(m) - 1;
  if (!y || monthIndex < 0 || monthIndex > 11) return '—';

  return `${TR_MONTHS[monthIndex]} ${y}`;
}

export function monthName(month: number): string {
  return TR_MONTHS[month - 1] ?? '—';
}

/**
 * "2026-01-31" biçimindeki İŞ TARİHİNİ yerel saatte ayrıştırır.
 *
 * `new Date("2026-01-31")` bunu UTC gece yarısı olarak yorumlar; UTC+3'te
 * saat farkı yüzünden tarih bir gün geriye kayabilir. Sözleşme bitiş
 * tarihinin bir gün erken görünmesi kabul edilebilir bir hata değildir.
 */
export function parseLocalDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// -----------------------------------------------------------------------------
// Kesin aritmetik
// -----------------------------------------------------------------------------

/**
 * İstemci tarafı para aritmetiği. Yalnızca "Önizleme" panelleri ve
 * tablo toplamları için — YETKİLİ hesaplar her zaman sunucuda yapılır.
 */
export const money = {
  add: (a: string, b: string): string => new Big(a).plus(b).toFixed(2),
  subtract: (a: string, b: string): string => new Big(a).minus(b).toFixed(2),
  multiply: (a: string, b: string | number): string => new Big(a).times(b).toFixed(2),
  /** Yüzde payı: tutar × (oran / 100) */
  percentOf: (amount: string, ratePercent: string | number): string =>
    new Big(amount).times(new Big(ratePercent).div(100)).toFixed(2),
  max: (a: string, b: string): string => (new Big(a).gte(b) ? new Big(a).toFixed(2) : new Big(b).toFixed(2)),
  sum: (values: readonly string[]): string =>
    values.reduce((acc, v) => acc.plus(v || '0'), new Big(0)).toFixed(2),
  isZero: (a: string): boolean => new Big(a || '0').eq(0),
  compare: (a: string, b: string): -1 | 0 | 1 => new Big(a).cmp(b) as -1 | 0 | 1,
};

// -----------------------------------------------------------------------------
// Türkçe metin
// -----------------------------------------------------------------------------

/**
 * Türkçe büyük harf. `toUpperCase()` KULLANILMAZ:
 * "istanbul".toUpperCase() → "ISTANBUL" (yanlış), doğrusu "İSTANBUL".
 */
export function upperTr(value: string): string {
  return value.toLocaleUpperCase('tr-TR');
}

export function lowerTr(value: string): string {
  return value.toLocaleLowerCase('tr-TR');
}

/** Türkçe alfabetik sıralama — varsayılan karşılaştırma ş/ğ/ı'yı yanlış sıralar. */
export const trCollator = new Intl.Collator('tr-TR', { sensitivity: 'base', numeric: true });

/** Ad-soyad veya marka adından baş harf üretir (avatar için). */
export function initials(name: string, count = 2): string {
  return upperTr(
    name
      .trim()
      .split(/\s+/)
      .slice(0, count)
      .map((w) => w.charAt(0))
      .join(''),
  );
}
